import { CollectionScheduledEmail, CollectionScheduleStatus } from './model';

export const toneChipProps = (
  tone: CollectionScheduledEmail['tone'],
): { label: string; color: 'error' | 'warning' | 'success' } => {
  if (tone === 'urgent') return { label: 'Urgent', color: 'error' };
  if (tone === 'second_notice') return { label: 'Second Notice', color: 'warning' };
  return { label: 'Friendly Reminder', color: 'success' };
};

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const relativeTimeFromNow = (iso: string | null): string => {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < MINUTE) return 'just now';
  if (diffMs < HOUR) {
    const m = Math.floor(diffMs / MINUTE);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (diffMs < DAY) {
    const h = Math.floor(diffMs / HOUR);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  const d = Math.floor(diffMs / DAY);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};

export const relativeTimeUntil = (iso: string | null): string => {
  if (!iso) return '';
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return 'now';
  if (diffMs < MINUTE) return 'less than a minute';
  if (diffMs < HOUR) {
    const m = Math.ceil(diffMs / MINUTE);
    return `${m} minute${m === 1 ? '' : 's'}`;
  }
  if (diffMs < DAY) {
    const h = Math.ceil(diffMs / HOUR);
    return `${h} hour${h === 1 ? '' : 's'}`;
  }
  const d = Math.ceil(diffMs / DAY);
  return `${d} day${d === 1 ? '' : 's'}`;
};

interface StatusChipProps {
  label: string;
  color: 'default' | 'success' | 'warning' | 'error' | 'info';
  tooltip: string;
}

export const scheduleStatusChip = (email: CollectionScheduledEmail): StatusChipProps => {
  const status: CollectionScheduleStatus = email.schedule_status;

  if (status === 'no_recipients') {
    return {
      label: 'No recipients',
      color: 'error',
      tooltip: 'No email addresses for this account.',
    };
  }

  if (status === 'auto_paused') {
    return {
      label: 'Automatic paused',
      color: 'warning',
      tooltip: 'Automatic sending is paused for this client. Use Resume automatic to re-enable.',
    };
  }

  if (status === 'auto_skipped') {
    const next = email.next_send_at ? ` Next auto send ${email.next_send_at}.` : '';
    return {
      label: 'Auto skipped — sent manually',
      color: 'info',
      tooltip: `Manual send already went out; the next automatic send for this cycle is skipped.${next}`,
    };
  }

  if (status === 'manual_locked') {
    return {
      label: `Manual locked (${relativeTimeUntil(email.manual_send_locked_until)})`,
      color: 'warning',
      tooltip: 'Manual send was used recently. Available again after a short cooldown.',
    };
  }

  const next = email.next_send_at ? ` Next auto send ${email.next_send_at}.` : '';
  return {
    label: 'Ready',
    color: 'success',
    tooltip: `This client is on the automatic schedule.${next}`,
  };
};

// Withhold attachments when at least one invoice lacks a matched PDF. Mirrors the backend rule in
// Collections::CollectionCandidate#all_invoices_have_matched_pdfs? so the UI can warn users before
// they send. invoice_count == 0 → no warning (no attachments section at all).
export const attachmentsWithheld = (email: CollectionScheduledEmail): boolean =>
  email.invoice_count > 0 && email.matched_pdf_count < email.invoice_count;
